/**
 * Menambahkan token dan penanganan 401 ke instans axios BAWAAN.
 *
 * KENAPA BERKAS INI ADA
 * Proyek ini punya dua cara memanggil API yang hidup berdampingan:
 *
 *   1. services/http.js  — instans khusus, token disisipkan otomatis
 *   2. `import axios from 'axios'` — instans bawaan, token harus ditempel
 *      sendiri di setiap panggilan
 *
 * Cara kedua dipakai di 49 berkas dengan ratusan panggilan. Menempelkan
 * header satu per satu berarti satu saja yang terlewat menghasilkan 401, dan
 * halamannya gagal tanpa penjelasan yang berguna. Hasil pemeriksaan: 12 berkas
 * TIDAK menyertakan token sama sekali, dan 16 berkas lain hanya sebagian.
 *
 * Contoh nyatanya: halaman Tournament Insight melakukan 13 panggilan API,
 * nol di antaranya membawa token. Ketiga belasnya dijawab 401, lalu blok
 * catch memanggil this.toast yang juga tidak ada — sehingga yang muncul di
 * layar adalah "Cannot read properties of undefined", pesan yang sama sekali
 * tidak menunjuk ke penyebab sebenarnya.
 *
 * Alih-alih menyunting 49 berkas dan berharap tidak ada yang terlewat,
 * interceptor dipasang pada instans bawaan. Satu tempat, berlaku untuk semua
 * panggilan, termasuk kode yang ditulis nanti.
 *
 * Panggilan yang SUDAH menempelkan header sendiri tidak terganggu: header
 * yang sudah ada tidak ditimpa.
 */

import axios from 'axios';
import API from '@/config/api';
import { getToken, clearSession } from './http';
import { laporGalat, perluDilaporkan } from './lapor-galat';

axios.defaults.baseURL = API.API_BASE_URL;
axios.defaults.timeout = 15000; // 15 detik batas waktu pada jaringan lemah

// Menandai instans supaya interceptor tidak terpasang dua kali bila berkas
// ini secara tidak sengaja diimpor lebih dari sekali.
if (!axios.__arspoTerpasang) {
  axios.__arspoTerpasang = true;

  axios.interceptors.request.use((config) => {
    const token = getToken();

    // Tidak menimpa header yang sudah ditulis pemanggil.
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Biarkan peramban menyusun boundary sendiri untuk unggahan berkas.
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  });

  let onUnauthorized = null;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        clearSession();
        if (onUnauthorized) onUnauthorized();
      }

      const isTimeout = error.code === 'ECONNABORTED' || (error.message && error.message.toLowerCase().includes('timeout'));
      const isNetworkError = !error.response && error.message && error.message.toLowerCase().includes('network');

      const data = error.response?.data;
      error.friendlyMessage =
        data?.message
        || data?.error
        || (isTimeout
          ? 'Koneksi internet lambat atau terputus. Silakan periksa jaringan dan coba lagi.'
          : (isNetworkError
            ? 'Tidak dapat terhubung ke server. Periksa sinyal internet Anda.'
            : (error.response ? 'Permintaan gagal diproses.' : 'Tidak dapat menghubungi peladen.')));

      /*
       * Kegagalan SELALU terlihat oleh pengguna.
       * 136 blok catch di proyek ini hanya menulis ke console.error, dan
       * konsol tidak pernah dibuka oleh panitia maupun pelatih. Lihat
       * catatan lengkap di services/lapor-galat.js.
       */
      if (perluDilaporkan(error)) laporGalat(error.friendlyMessage);

      return Promise.reject(error);
    }
  );

  axios.setUnauthorizedHandler = (handler) => {
    onUnauthorized = handler;
  };
}

export default axios;
